import { create } from 'zustand';
import { CompetitorInterface } from 'Services/Query/Competitors/Types';
import { Product } from '@setvi/shared/interfaces';

import { RFQProducts } from '../interfaces';

export interface AnalyticsProps {
  time: string;
  processed: number;
  matched: number;
  notMatched: number;
  onlineMatched: number;
  mfgMatched: number;
  nonMfgMatched: number;
  fileProcessingTime: number;
  exactMatch?: number;
  possibleMatch?: number;
  closeMatch?: number;
  privateLabelMatched?: number;
  privateLabelSwapped?: number;
  markedAsNonMatched?: number;
  markedAsNonMatch?: number;
  removedFromQuotes?: number;
  userSelection?: number;
}

interface RankingValues {
  enablePrivateLabelRanking: boolean;
  enableStockProductRanking: boolean;
  enableVendorRanking: boolean;
}

interface AdditionalParams {
  enablePostProcessing?: boolean;
  enableMatchProductBySku?: boolean;
  enableFindProductOnline?: boolean;
  enableGettingCompetitorProductDescription?: boolean;
}

export interface ProcessingTotals {
  all: number;
  matched: number;
  notMatched: number;
  mfgSkuMatched: number;
  foundOnline: number;
  nonMfgSkuMatched: number;
  exactMatch: number;
  possibleMatch: number;
  closeMatch: number;
  privateLabelMatched?: number;
  privateLabelSwapped?: number;
  markedAsNonMatched?: number;
  removedFromQuote?: number;
  userSelection?: number;
}

export interface FileInfo {
  id: string;
  analytics?: ProcessingTotals;
  statusId?: string;
  status:
    | 'UPLOADING'
    | 'AWAITING_PROCESSING'
    | 'PROCESSING'
    | 'COMPLETED'
    | 'PENDING'
    | 'FAILED';
  file: {
    url: string | null;
    name: string;
    size?: number;
    type?: string;
    total?: number;
  };
}

export interface RFQStoreStates {
  // RFQ Details
  description: string;
  name: string;
  competitor: CompetitorInterface;

  // quote related details
  quoteId?: string;
  // customer related details
  customerId?: string;
  locationId?: string;
  // prospect related details
  segmentId?: string;
  prospect?: {
    name?: string;
    address?: {
      zip?: string;
      state?: string;
      city?: string;
      street?: string;
    };
    contact?: {
      phone?: string;
      email?: string;
      name?: string;
    };
  };

  // Products & Processing
  products: RFQProducts[];
  processedProducts: RFQProducts[];
  // rfqItem ids changed by another collaborator that need a full enriched
  // refetch from the page endpoint (the realtime broadcast is lean and lacks
  // catalog enrichment like image / active status). Consumed + cleared by the
  // content layer once fresh page data arrives.
  remoteRefreshIds: string[];
  selectedProducts: Product[];
  productDetails: Product | null;
  // The rfqItem id the product-details dialog is currently scoped to.
  // Tracked separately from `productDetails` because that field holds the
  // matched internal product (its `id` is the catalog id, not the rfqItem id),
  // and Spaces locations/locks key off the rfqItem.
  activeRfqItemId: string | null;
  // The rfqItem id currently hovered. Cheaper signal than the dialog state —
  // broadcast as fallback when no dialog/modal claims focus, so other
  // members can see whose mouse is on which row.
  hoveredRfqItemId: string | null;
  // One-shot request to scroll a row into view (e.g. clicking a collaborator's
  // header avatar jumps to the row they're on). Consumed + cleared by content.
  scrollToRfqItemId: string | null;

  // File Management
  files: FileInfo[];
  currentFileId: string | null;
  selectedFile: string | null;
  currentJobId: string | null;

  // Processing State
  uploading: boolean;
  processing: boolean;
  polling: boolean;
  completed: boolean;
  isDialogOpen: boolean;
  isSocketActive: boolean;
  isSorting: boolean;

  // Analytics & Totals
  analytics: AnalyticsProps;
  totalsToUpdate: Partial<ProcessingTotals>;
  processingTime: string;
  privateLabelsSelected: string[];

  // Configuration
  rankings: RankingValues;
  params: AdditionalParams;

  // We will use this to filter analytics instead of files
  filterAnalytics?: AnalyticsProps;
}

interface Store extends RFQStoreStates {
  // Core Updates
  updateCompetitor: (newCompetitor: CompetitorInterface) => void;
  updateAdditionalParams: (params: AdditionalParams) => void;
  updateAnalytics: (newAnalytics: AnalyticsProps) => void;
  updateAnalytic: (newAnalytics: Partial<AnalyticsProps>) => void;
  updateProducts: (newProducts: RFQProducts[]) => void;
  setProducts: (newProducts: RFQProducts[]) => void;
  addProducts: (newProducts: RFQProducts[]) => void;
  updateProcessedProducts: (newProducts: RFQProducts[]) => void;
  appendProcessedProducts: (newProcessedProducts: RFQProducts) => void;
  updateProcessedProduct: (
    id: string,
    updatedProduct: Partial<RFQProducts>
  ) => void;
  markRemoteRefresh: (id: string) => void;
  clearRemoteRefresh: (ids: string[]) => void;
  updateSelectedProducts: (products: Product[]) => void;
  updateProductDetails: (product: Product | null) => void;
  updateActiveRfqItemId: (rfqItemId: string | null) => void;
  updateHoveredRfqItemId: (rfqItemId: string | null) => void;
  requestScrollToRfqItem: (rfqItemId: string | null) => void;
  updateRankings: (newRankings: RankingValues) => void;
  updateDescription: (newDescription: string) => void;
  updateCompleted: (newCompleted: boolean) => void;
  updateProcessing: (newProcessing: boolean) => void;
  updatePolling: (newPolling: boolean) => void;
  updateName: (newName: string) => void;
  updateTotalsToUpdate: (totals: Partial<ProcessingTotals>) => void;
  updateProcessingTime: (time: string) => void;
  updatePrivateLabelsSelected: (ids: string[]) => void;
  updateSorting: (newSorting: boolean) => void;

  updateQuoteId: (quoteId: string) => void;
  updateCustomerId: (customerId: string) => void;
  updateLocationId: (locationId: string) => void;
  updateSegmentId: (segmentId: string) => void;
  updateProspect: (prospect: {
    address?: {
      zip?: string;
      state?: string;
      city?: string;
      street?: string;
    };
    contact?: {
      phone?: string;
      email?: string;
      name?: string;
    };
    name?: string;
  }) => void;

  // File Management
  updateFileUrls: (files: FileInfo[]) => void;
  addFileUrl: (file: FileInfo) => void;
  removeFileUrl: (index: number) => void;
  updateFile: (id: string, file: Partial<FileInfo>) => void;
  updateSelectedFile: (fileName: string | null) => void;

  // Dialog State
  updateDialogOpen: (isOpen: boolean) => void;

  // Batch Update
  batchUpdate: (updates: Partial<RFQStoreStates>) => void;

  updateFilterAnalytics: (
    analytics: Partial<AnalyticsProps> | undefined
  ) => void;

  reset: () => void;
}

const initAnalytics = {
  time: '',
  fileProcessingTime: 0,
  notMatched: 0,
  onlineMatched: 0,
  mfgMatched: 0,
  matched: 0,
  processed: 0,
  nonMfgMatched: 0,
  exactMatch: 0,
  possibleMatch: 0,
  closeMatch: 0,
  removedFromQuote: 0,
  privateLabelMatched: 0,
  privateLabelSwapped: 0,
  markedAsNonMatched: 0,
  userSelection: 0
};

const initalState: RFQStoreStates = {
  // RFQ Details
  name: '',
  description: '',
  competitor: null,

  // Quote related details
  quoteId: null,
  customerId: null,
  locationId: null,
  segmentId: null,
  prospect: null,

  // Products & Processing
  products: [],
  processedProducts: [],
  remoteRefreshIds: [],
  selectedProducts: [],
  productDetails: null,
  activeRfqItemId: null,
  hoveredRfqItemId: null,
  scrollToRfqItemId: null,

  // File Management
  files: [],
  selectedFile: null,
  currentFileId: null,
  currentJobId: null,

  // Processing State
  uploading: false,
  processing: false,
  polling: false,
  completed: false,
  isDialogOpen: false,
  isSocketActive: false,
  isSorting: false,

  // Analytics & Totals
  filterAnalytics: initAnalytics,
  analytics: initAnalytics,

  totalsToUpdate: {
    exactMatch: 0,
    possibleMatch: 0,
    closeMatch: 0,
    privateLabelSwapped: 0
  },
  processingTime: '',
  privateLabelsSelected: [],

  // Configuration
  rankings: {
    enablePrivateLabelRanking: true,
    enableStockProductRanking: true,
    enableVendorRanking: true
  },
  params: {
    enablePostProcessing: true,
    enableMatchProductBySku: true,
    enableFindProductOnline: true,
    enableGettingCompetitorProductDescription: false
  }
};

export const useRFQStore = create<Store>(set => ({
  ...initalState,

  // Core Updates
  updateCompetitor: (newCompetitor: CompetitorInterface) =>
    set({ competitor: newCompetitor }),
  updateAdditionalParams: (newParams: AdditionalParams) =>
    set({ params: newParams }),
  updateCompleted: (newCompleted: boolean) => set({ completed: newCompleted }),
  updateAnalytics: (newAnalytics: AnalyticsProps) =>
    set(state => ({ analytics: { ...state?.analytics, ...newAnalytics } })),
  updateAnalytic: (newAnalytics: Partial<AnalyticsProps>) =>
    set(state => ({ analytics: { ...state.analytics, ...newAnalytics } })),
  updateDescription: (newDescription: string) =>
    set({ description: newDescription }),
  updateProducts: (newProducts: RFQProducts[]) =>
    set({ products: newProducts }),
  setProducts: (newProducts: RFQProducts[]) => set({ products: newProducts }),
  addProducts: (newProducts: RFQProducts[]) =>
    set(state => ({ products: [...state.products, ...newProducts] })),
  appendProcessedProducts: (newProcessedProducts: RFQProducts) =>
    set(state => {
      // Dedupe by productParsedId (the parser Guid set once at parse time and
      // carried on every Ably event for the product's lifetime). Falls back to
      // `id` (Mongo _id, present after save) when the parser Guid is missing.
      // Each subsequent stream event for the same product is a richer view of
      // the same row — merge fresh fields into the existing entry instead of
      // appending a duplicate.
      const incoming: any = newProcessedProducts;
      const incomingKey = incoming?.productParsedId || incoming?.id;

      const upsert = (list: RFQProducts[]) => {
        if (!incomingKey) return [...list, newProcessedProducts];
        const idx = list.findIndex(
          (p: any) => (p?.productParsedId || p?.id) === incomingKey
        );
        if (idx === -1) return [...list, newProcessedProducts];
        const next = list.slice();
        next[idx] = { ...list[idx], ...newProcessedProducts };
        return next;
      };

      return {
        products: upsert(state.products),
        processedProducts: upsert(state.processedProducts)
      };
    }),
  updateProcessedProducts: (newProducts: RFQProducts[]) =>
    set({
      processedProducts: newProducts,
      products: newProducts // Also update products for backward compatibility
    }),
  updateProcessedProduct: (id: string, updatedProduct: Partial<RFQProducts>) =>
    set(state => {
      const updatedProcessedProducts = state.processedProducts.map(product =>
        product.id === id ? { ...product, ...updatedProduct } : product
      );
      return {
        processedProducts: updatedProcessedProducts,
        products: updatedProcessedProducts // Also update products for backward compatibility
      };
    }),
  markRemoteRefresh: (id: string) =>
    set(state =>
      state.remoteRefreshIds.includes(id)
        ? state
        : { remoteRefreshIds: [...state.remoteRefreshIds, id] }
    ),
  clearRemoteRefresh: (ids: string[]) =>
    set(state => {
      if (!ids.length) return state;
      const toClear = new Set(ids);
      const remaining = state.remoteRefreshIds.filter(id => !toClear.has(id));
      return remaining.length === state.remoteRefreshIds.length
        ? state
        : { remoteRefreshIds: remaining };
    }),
  updateSelectedProducts: (products: Product[]) =>
    set({ selectedProducts: products }),
  updateProductDetails: (product: Product | null) =>
    set({ productDetails: product }),
  updateActiveRfqItemId: (rfqItemId: string | null) =>
    set({ activeRfqItemId: rfqItemId }),
  updateHoveredRfqItemId: (rfqItemId: string | null) =>
    set({ hoveredRfqItemId: rfqItemId }),
  requestScrollToRfqItem: (rfqItemId: string | null) =>
    set({ scrollToRfqItemId: rfqItemId }),
  updateProcessing: (newProcessing: boolean) =>
    set({ processing: newProcessing }),
  updatePolling: (newPolling: boolean) => set({ polling: newPolling }),
  updateName: (newName: string) => set({ name: newName }),
  updateRankings: (newRankings: RankingValues) =>
    set({ rankings: newRankings }),
  updateTotalsToUpdate: (totals: Partial<ProcessingTotals>) =>
    set(state => ({ totalsToUpdate: { ...state.totalsToUpdate, ...totals } })),
  updateProcessingTime: (time: string) => set({ processingTime: time }),
  updatePrivateLabelsSelected: (ids: string[]) =>
    set(state => ({
      privateLabelsSelected: [...state.privateLabelsSelected, ...ids]
    })),
  updateSorting: (newSorting: boolean) => set({ isSorting: newSorting }),

  updateQuoteId: (quoteId: string) => set({ quoteId }),
  updateCustomerId: (customerId: string) => set({ customerId }),
  updateLocationId: (locationId: string) => set({ locationId }),
  updateSegmentId: (segmentId: string) => set({ segmentId }),
  updateProspect: (prospect: {
    address?: {
      zip?: string;
      state?: string;
      city?: string;
      street?: string;
    };
    contact?: {
      phone?: string;
      email?: string;
      name?: string;
    };
    name?: string;
  }) => set({ prospect }),

  // File Management
  updateFileUrls: (files: FileInfo[]) => {
    set({ files });
  },
  updateFile: (id: string, file: Partial<FileInfo>) =>
    set(state => {
      const updatedFiles = state.files.map(f =>
        f.id === id ? { ...f, ...file } : f
      );

      return {
        files: updatedFiles
      };
    }),
  addFileUrl: (file: FileInfo) => {
    set(state => ({ files: [...state.files, file] }));
  },
  removeFileUrl: (index: number) =>
    set(state => ({
      files: state.files.filter((_, i) => i !== index)
    })),
  updateSelectedFile: (fileName: string | null) =>
    set({ selectedFile: fileName }),

  // Dialog State
  updateDialogOpen: (isOpen: boolean) => set({ isDialogOpen: isOpen }),

  // Batch Update
  batchUpdate: (updates: Partial<RFQStoreStates>) => {
    set(updates);
  },

  updateFilterAnalytics: (analytics: Partial<AnalyticsProps> | undefined) =>
    set(state => ({
      filterAnalytics: {
        ...state?.filterAnalytics,
        ...analytics
      } as AnalyticsProps
    })),

  reset: () => set({ ...initalState })
}));
